import { createAsyncThunk } from '@reduxjs/toolkit';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/dashboard/dashboard.response.type';
import * as Request from '@shared/models/dashboard/dashboard.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setSkillProgress, setTrackProgress, setSpentTime, setRankComparison } from '../slices/dashboard.slice';
import { fetchPopularEventList, fetchUpcomingEventList } from '../../event/queries';
import { fetchLatestBadges } from '../../showcase/queries';
import { fetchAssesmentResults } from './baselineassesment.query';

export const DashboardEndpoints = {
    getRankComparison: config.base_url + '/ranks/compare',
    getDashboardData: config.base_url + '/dashboard',
    getSkillProgress: config.base_url + '/analysis/skills?isResourceCategory=false',
    getTrackProgress: config.base_url + '/analysis/tracks',
    getTimeSpent: config.base_url + '/time-spent/learning-hours',
};

export const fetchRankComparison = createAsyncThunk<void, Request.IRankComparisonQueryType>(
    DashboardEndpoints.getRankComparison,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IRankComparisonResponseType>>(
                DashboardEndpoints.getRankComparison
            );

            dispatch(setRankComparison(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (error) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(error);
            }
            console.log(
                formatErrorMessage(
                    error,
                    `Error fetching ranks comparison, query: ${DashboardEndpoints.getRankComparison}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchSkillsProgress = createAsyncThunk<void, Request.ISkillsProgressQueryType>(
    DashboardEndpoints.getSkillProgress,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.ISkillsProgressResponseType>>(
                `${DashboardEndpoints.getSkillProgress}`
            );

            dispatch(setSkillProgress(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching skill progress of dashboard, query: ${DashboardEndpoints.getSkillProgress}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchTrackProgress = createAsyncThunk<void, Request.ITrackProgressQueryType>(
    DashboardEndpoints.getTrackProgress,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.ITracksProgressResponseType>>(
                `${DashboardEndpoints.getTrackProgress}`
            );

            dispatch(setTrackProgress(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching track progress of dashboard, query: ${DashboardEndpoints.getTrackProgress}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchSpentTime = createAsyncThunk<void, Request.ISpentTimeQueryType>(
    DashboardEndpoints.getTimeSpent,
    async (query, { dispatch }) => {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await httpService.get<AscResponse<Response.ISpentTimeResponseType>>(
                `${DashboardEndpoints.getTimeSpent}?timezone=${timezone}`
            );

            dispatch(setSpentTime(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching time spent of a user, query: ${DashboardEndpoints.getTimeSpent}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchPlatformDashboardData = createAsyncThunk<void>(
    DashboardEndpoints.getDashboardData,
    async (query, { dispatch }) => {
        try {
            dispatch(fetchRankComparison({}));
            dispatch(fetchPopularEventList({}));
            dispatch(fetchUpcomingEventList({}));
            dispatch(fetchSkillsProgress({}));
            dispatch(fetchTrackProgress({}));
            dispatch(fetchSpentTime({}));
            dispatch(fetchLatestBadges({}));
            dispatch(fetchAssesmentResults({}));
        } catch (err) {
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching dashboard data of platform, query: ${DashboardEndpoints.getDashboardData}`
                ),
                { type: 'error' }
            );
        }
    }
);
